import React from "react";
import { DateTime } from "luxon";
import { trpc } from "../../main/components/ProviderContainer.tsx";
import ImportedFactureReviewPanel from "../components/ImportedFactureReviewPanel.tsx";
import ConfirmModal from "../components/ConfirmModal.tsx";
import { Trash } from "react-bootstrap-icons";
import InvoiceAnalysisBadge from "../components/InvoiceAnalysisBadge.tsx";
import { shouldStartImportedInvoiceAnalysis } from "../components/importedInvoiceReviewState.ts";

const formatDateTime = (date: Date) => {
  return DateTime.fromJSDate(date).toFormat("dd/MM/yyyy HH:mm");
};

const ImportedFacturesContainer = () => {
  const { data: importedDocs, isLoading, error, refetch } =
    trpc.facture.getImportedInbox.useQuery(undefined, {
      refetchInterval: (query) =>
        query.state.data?.some((doc) =>
          !doc.reviewDraft && (doc.analysisStatus === "analyzing" || doc.analysisStatus === "pending"),
        ) ? 2000 : false,
    });
  const utils = trpc.useUtils();
  const [selectedDocId, setSelectedDocId] = React.useState<string | null>(null);
  const [editingDocId, setEditingDocId] = React.useState<string | null>(null);
  const [deletingDocId, setDeletingDocId] = React.useState<string | null>(null);
  const autoAnalysisMutation = trpc.facture.analyzeImportedDoc.useMutation({
    onSettled: async () => {
      await utils.facture.invalidate();
    },
  });
  const deleteMutation = trpc.facture.deletePendingImportedDoc.useMutation({
    onSuccess: async (_, docId) => {
      await utils.facture.invalidate();
      setSelectedDocId((current) => (current === docId ? null : current));
      setDeletingDocId(null);
    },
  });

  const selectedDoc =
    importedDocs?.find((doc) => doc._id === selectedDocId) || null;
  const deletingDoc =
    importedDocs?.find((doc) => doc._id === deletingDocId) || null;
  const isSelectedAnalysisPending =
    selectedDoc && !selectedDoc.reviewDraft && !selectedDoc.analysis && editingDocId !== selectedDoc._id && (
      selectedDoc.analysisStatus === "analyzing" ||
      (autoAnalysisMutation.isPending &&
        autoAnalysisMutation.variables?.docId === selectedDoc._id)
    );

  const reviewPanelAnchorRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      node?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [],
  );

  if (isLoading) {
    return <div className="p-3">Chargement des factures à traiter...</div>;
  }

  return (
    <div className="container-fluid px-0">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="fw-bold">Factures à traiter</div>
          <span className="badge text-bg-secondary">
            {importedDocs?.length || 0}
          </span>
        </div>
        <div className="card-body">
          <p className="text-muted">
            Retrouvez ici les PDF reçus par email ou importés manuellement, y
            compris les imports en lot interrompus. Une facture quitte cette
            liste après enregistrement, ajout aux règlements en attente ou rejet.
          </p>
          {error ? (
            <div className="alert alert-danger" role="alert">
              Impossible de charger les factures à traiter : {error.message}{" "}
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => void refetch()}
              >
                Réessayer
              </button>
            </div>
          ) : null}
          {deleteMutation.error ? (
            <div className="alert alert-danger" role="alert">
              {deleteMutation.error.message}
            </div>
          ) : null}
          {!error && (!importedDocs || importedDocs.length === 0) ? (
            <div className="text-muted">
              Aucune facture à traiter.
            </div>
          ) : importedDocs && importedDocs.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Fichier</th>
                    <th>Email source</th>
                    <th>Importée le</th>
                    <th>Source</th>
                     <th>Statut</th>
                    <th>Analyse OCR</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {importedDocs.map((doc) => (
                    <tr
                      key={doc._id}
                      className={
                        selectedDocId === doc._id ? "table-primary" : ""
                      }
                    >
                      <td>{doc.originalFileName}</td>
                      <td>
                        <div>{doc.sourceEmailSender || "-"}</div>
                        <div className="text-muted small">
                          {doc.sourceEmailSubject || "-"}
                        </div>
                      </td>
                      <td>{formatDateTime(doc.uploadedAt)}</td>
                      <td>
                        <span className="badge text-bg-light">
                          {doc.source === "manual-upload"
                            ? "Import PDF"
                            : doc.source === "cadendar-email"
                              ? "Email Companion"
                              : "Import PDF (ancien)"}
                        </span>
                      </td>
                      <td>
                        {doc.invoiceToPay ? (
                          <span className="badge text-bg-warning">À payer</span>
                        ) : (
                          <span className="badge text-bg-secondary">
                            À traiter
                          </span>
                        )}
                      </td>
                      <td>
                        {doc.reviewDraft ? <span className="badge text-bg-info">Informations conservées</span> : <InvoiceAnalysisBadge
                          status={doc.analysisStatus}
                          error={doc.analysisError}
                        />}
                      </td>
                      <td className="text-end">
                        <div className="d-inline-flex gap-2">
                          <a
                            className="btn btn-outline-secondary btn-sm"
                            href={doc.pdfUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Voir PDF
                          </a>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={deleteMutation.isPending}
                            onClick={() => {
                              if (selectedDocId === doc._id) return;
                              setSelectedDocId(doc._id);
                              setEditingDocId(null);
                              autoAnalysisMutation.reset();
                              if (shouldStartImportedInvoiceAnalysis(doc)) {
                                autoAnalysisMutation.mutate({ docId: doc._id });
                              }
                            }}
                          >
                            Traiter
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            title="Supprimer la facture importée"
                            aria-label="Supprimer la facture importée"
                            disabled={deleteMutation.isPending}
                            onClick={() => setDeletingDocId(doc._id)}
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>

      {selectedDoc ? (
        <div key={selectedDoc._id} ref={reviewPanelAnchorRef}>
          {autoAnalysisMutation.error &&
          autoAnalysisMutation.variables?.docId === selectedDoc._id &&
          selectedDoc.analysisStatus !== "done" && !selectedDoc.analysisError ? (
            <div className="alert alert-danger mt-3" role="alert">
              {autoAnalysisMutation.error.message}
            </div>
          ) : null}
          {isSelectedAnalysisPending ? (
            <div className="card mt-4">
              <div className="card-body" role="status">
                Analyse automatique de {selectedDoc.originalFileName} en cours…
                Le formulaire sera prérempli dès que le résultat sera disponible.
                <button
                  type="button"
                  className="btn btn-outline-secondary ms-3"
                  onClick={() => setSelectedDocId(null)}
                >
                  Fermer
                </button>
              </div>
            </div>
          ) : (
            <div onFocusCapture={() => setEditingDocId(selectedDoc._id)}>
              <ImportedFactureReviewPanel
                selectedDoc={selectedDoc}
                onClose={() => setSelectedDocId(null)}
                onCreated={() => setSelectedDocId(null)}
                onAnalysisStart={() => setEditingDocId(selectedDoc._id)}
              />
            </div>
          )}
        </div>
      ) : null}
      <ConfirmModal
        show={deletingDoc != null}
        title="Supprimer la facture importée"
        onCancel={() => setDeletingDocId(null)}
        onConfirm={() => {
          if (deletingDocId) {
            deleteMutation.mutate(deletingDocId);
          }
        }}
        confirmLabel="Supprimer"
        submittingLabel="Suppression..."
        confirmVariant="danger"
        isSubmitting={deleteMutation.isPending}
      >
        <p className="mb-0">
          {deletingDoc
            ? `Supprimer définitivement ${deletingDoc.originalFileName} ?`
            : "Supprimer définitivement cette facture importée ?"}
        </p>
      </ConfirmModal>
    </div>
  );
};

export default ImportedFacturesContainer;
